import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Waves, Clock, CheckCircle, Eye, EyeOff, ScrollText, XCircle, KeyRound, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { CharteContent } from './CharteUtilisation';
import { PortesAccueil } from '../components/login/PortesAccueil';
import { ChoixRolePro, ROLES_PRO } from '../components/login/ChoixRolePro';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  // accueil (3 portes) | pro (choix du rôle) | inscription (formulaire pro)
  const [vue, setVue] = useState('accueil');
  const isRegister = vue === 'inscription';
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationPending, setRegistrationPending] = useState(false);
  const [charteAccepted, setCharteAccepted] = useState(false);
  const [showCharte, setShowCharte] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nom: '',
    prenom: '',
    role: 'formateur',
    telephone: ''
  });
  useEffect(() => {
    setFormData(f => ({ ...f, password: '' }));
    setCharteAccepted(false);
    setShowPassword(false);
  }, [vue]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isRegister && !charteAccepted) {
      toast.error('Vous devez accepter la charte d\'utilisation pour vous inscrire');
      return;
    }
    if (isRegister) {
      const pwd = formData.password;
      if (pwd.length < 8 || !/[A-Z]/.test(pwd) || !/[0-9]/.test(pwd)) {
        toast.error('Le mot de passe ne respecte pas les critères de sécurité');
        return;
      }
    }
    setLoading(true);

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const payload = isRegister ? formData : { email: formData.email, password: formData.password };
      
      const response = await axios.post(endpoint, payload);
      
      if (isRegister && response.data.status === 'en_attente') {
        // Inscription en attente de validation
        setRegistrationPending(true);
        toast.success('Inscription enregistrée !');
      } else {
        toast.success(isRegister ? 'Inscription réussie !' : 'Connexion réussie !');
        onLogin(response.data.access_token, response.data.user);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLogin = async (email, password) => {
    setLoading(true);
    try {
      const response = await axios.post('/auth/login', { email, password });
      toast.success('Connexion réussie !');
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Login puis redirection vers une page spécifique
  const handleQuickLoginWithRedirect = async (email, password, redirectPath) => {
    setLoading(true);
    try {
      const response = await axios.post('/auth/login', { email, password });
      toast.success('Connexion réussie !');
      onLogin(response.data.access_token, response.data.user);
      // Redirection après un court délai pour laisser le state se mettre à jour
      setTimeout(() => {
        navigate(redirectPath);
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Affichage si inscription en attente
  if (registrationPending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="h-8 w-8 text-orange-600" />
          </div>
          <h1 className="text-2xl font-display font-bold text-[#0F172A] mb-2">
            Inscription en attente
          </h1>
          <p className="text-slate-600 mb-6">
            Votre demande d'inscription a été enregistrée et est <strong>en attente de validation</strong> par le centre de formation.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-blue-700">
              <strong>Prochaines étapes :</strong>
            </p>
            <ul className="text-sm text-blue-600 mt-2 space-y-1">
              <li>✓ Vous recevrez un email de confirmation</li>
              <li>✓ Le centre validera votre inscription sous 24-48h</li>
              <li>✓ Vous serez notifié par email une fois validé</li>
            </ul>
          </div>
          <Button
            onClick={() => {
              setRegistrationPending(false);
              setFormData(f => ({ ...f, email: '', nom: '', prenom: '', telephone: '' }));
              setVue('accueil');
            }}
            className="w-full bg-[#8DC63F] hover:bg-[#7db336]"
          >
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex justify-center p-5 sm:p-8 bg-white">
        <div className={`w-full ${vue === 'pro' ? 'max-w-xl' : 'max-w-md'} space-y-6 my-auto`}>
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-md border border-slate-100 shrink-0">
              <img src="/logo-fisl.png" alt="FISL Logo" className="h-12 w-12 object-contain" />
            </div>
            <div className="min-w-0">
              <h1 className="text-3xl sm:text-4xl font-display font-bold text-[#0F172A] leading-none">FISL</h1>
              <p className="text-sm text-slate-600 mt-1">Formation Internationale aux Sports de Loisirs</p>
              <p className="text-xs text-slate-500">BPJEPS Activités de Motonautisme et Disciplines Associées</p>
            </div>
          </div>

          {vue === 'accueil' && <PortesAccueil onPro={() => setVue('pro')} />}
          {vue === 'pro' && <ChoixRolePro onRetour={() => setVue('accueil')} onChoix={(role) => { setFormData({ ...formData, role }); setVue('inscription'); }} />}

          {vue !== 'pro' && (
          <div className={vue === 'accueil' ? 'rounded-2xl border-2 border-slate-200 bg-slate-50/60 p-5' : ''} data-testid={vue === 'accueil' ? 'porte-connexion' : 'inscription-pro'}>
            {vue === 'accueil' ? (
              <div className="flex items-center gap-2 mb-4">
                <KeyRound className="h-5 w-5 text-[#8DC63F]" />
                <h2 className="text-lg font-display font-bold text-[#0F172A]">J'ai déjà un compte</h2>
              </div>
            ) : (
              <div className="space-y-3 mb-5">
                <button type="button" onClick={() => setVue('pro')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F172A]" data-testid="retour-choix-role">
                  <ArrowLeft className="h-4 w-4" /> Retour
                </button>
                <div className="flex flex-wrap items-center gap-2 rounded-xl bg-[#0F172A] px-4 py-3 text-white" data-testid="role-choisi">
                  <span className="text-sm text-white/70">Vous créez un compte :</span>
                  <span className="font-semibold" data-testid="role-choisi-label">{ROLES_PRO.find(r => r.value === formData.role)?.label}</span>
                  <button type="button" onClick={() => setVue('pro')} className="ml-auto text-xs font-semibold text-[#8DC63F] hover:underline" data-testid="changer-role">Changer</button>
                </div>
              </div>
            )}

          <form onSubmit={handleSubmit} className={vue === 'accueil' ? 'space-y-4' : 'space-y-5'} data-testid={vue === 'accueil' ? 'login-form' : 'register-form'}>
            {isRegister && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="prenom">Prénom</Label>
                    <Input
                      id="prenom"
                      type="text"
                      required
                      value={formData.prenom}
                      onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                      data-testid="input-prenom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nom">Nom</Label>
                    <Input
                      id="nom"
                      type="text"
                      required
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      data-testid="input-nom"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telephone">Téléphone</Label>
                  <Input
                    id="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    placeholder="+33 6 12 34 56 78"
                    data-testid="input-telephone"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="votre@email.com"
                data-testid="input-email"
              />
            </div>

            <div className="space-y-2 relative">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mot de passe</Label>
                {!isRegister && (
                  <Link 
                    to="/forgot-password" 
                    className="text-xs text-[#8DC63F] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </Link>
                )}
              </div>
              <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="pr-11"
                data-testid="input-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
                data-testid="toggle-password-visibility"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              </div>
              {isRegister && formData.password.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="password-strength">
                  {[
                    { ok: formData.password.length >= 8, text: '8 caractères minimum' },
                    { ok: /[A-Z]/.test(formData.password), text: '1 lettre majuscule' },
                    { ok: /[0-9]/.test(formData.password), text: '1 chiffre' },
                  ].map(({ ok, text }) => (
                    <div key={text} className={`flex items-center gap-1.5 text-xs ${ok ? 'text-green-600' : 'text-red-500'}`}>
                      {ok ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                      {text}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {isRegister && (
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={charteAccepted}
                  onChange={(e) => setCharteAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#8DC63F] focus:ring-[#8DC63F]"
                  data-testid="charte-register-checkbox"
                  id="charte-checkbox"
                />
                <label htmlFor="charte-checkbox" className="text-sm text-slate-600">
                  J'ai lu et j'accepte la{' '}
                  <button type="button" onClick={() => setShowCharte(true)} className="text-[#8DC63F] hover:underline font-medium">
                    charte d'utilisation
                  </button>{' '}
                  de la plateforme FISL *
                </label>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-[#8DC63F] hover:bg-[#7db336] text-white font-semibold h-11"
              disabled={loading || (isRegister && !charteAccepted)}
              data-testid="submit-button"
            >
              {loading ? 'Chargement...' : (isRegister ? "S'inscrire" : 'Se connecter')}
            </Button>

          </form>
          </div>
          )}

          {vue === 'accueil' && (
            <p className="text-xs text-slate-500 text-center" data-testid="mention-stagiaire">
              Déjà stagiaire FISL ? Connectez-vous ci-dessus avec les identifiants reçus du centre.
            </p>
          )}
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden lg:block bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1563172428-52ee6ec61b8c?w=1920&q=85&fit=crop)' }}
      >
        <div className="absolute inset-0 bg-[#8DC63F]/20 backdrop-blur-sm"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <Waves className="h-24 w-24 mx-auto mb-6 opacity-90" />
            <h2 className="text-5xl font-display font-bold mb-4">Préparez-vous à réussir</h2>
            <p className="text-xl opacity-90">Votre parcours professionnel commence ici</p>
          </div>
        </div>
      </div>

      {/* Modal charte pour lecture pendant inscription */}
      {showCharte && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="charte-read-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                <ScrollText className="h-5 w-5 text-[#8DC63F]" />
                Charte d'utilisation
              </h2>
              <button onClick={() => setShowCharte(false)} className="text-slate-400 hover:text-slate-600 text-xl font-bold">x</button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <CharteContent />
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end flex-shrink-0">
              <Button onClick={() => { setShowCharte(false); setCharteAccepted(true); }} className="bg-[#8DC63F] hover:bg-[#7AB62F] text-white" data-testid="charte-read-accept">
                J'ai lu la charte
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;